import { Component } from '@theme/component';

// Validate each outline separately; overlaps and out-of-bounds exclusions are clipped when measuring.
const EPS = 1e-12;
const cross = (a, b, c) => (b.lng-a.lng)*(c.lat-a.lat)-(b.lat-a.lat)*(c.lng-a.lng);
const onSegment = (a,b,p) => Math.abs(cross(a,b,p)) < EPS && p.lng >= Math.min(a.lng,b.lng)-EPS && p.lng <= Math.max(a.lng,b.lng)+EPS && p.lat >= Math.min(a.lat,b.lat)-EPS && p.lat <= Math.max(a.lat,b.lat)+EPS;
function intersects(a,b,c,d) {
  const x=cross(a,b,c),y=cross(a,b,d),z=cross(c,d,a),w=cross(c,d,b);
  return ((x>EPS && y < -EPS || x < -EPS && y>EPS) && (z>EPS && w < -EPS || z < -EPS && w>EPS)) || onSegment(a,b,c) || onSegment(a,b,d) || onSegment(c,d,a) || onSegment(c,d,b);
}
export function containsPoint(point, path) {
  let inside=false;
  for(let i=0,j=path.length-1;i<path.length;j=i++) {
    const a=path[j],b=path[i];
    if(onSegment(a,b,point)) return true;
    if((a.lat>point.lat)!==(b.lat>point.lat) && point.lng<(b.lng-a.lng)*(point.lat-a.lat)/(b.lat-a.lat)+a.lng) inside=!inside;
  }
  return inside;
}
/** Only malformed individual outlines prevent measuring; boundaries between shapes may touch. */
export function validateLawnShapes(shapes) {
  if(!shapes.some(s=>s.kind==='lawn')) return 'need_lawn';
  for(const {path} of shapes) {
    if(path.length<3 || path.some(p=>!Number.isFinite(p.lat)||!Number.isFinite(p.lng)||Math.abs(p.lat)>85||Math.abs(p.lng)>180)) return 'invalid_shape';
    let area=0;
    for(let i=0;i<path.length;i++) {
      const a=path[i],b=path[(i+1)%path.length];
      if(Math.hypot(a.lng-b.lng,a.lat-b.lat)<EPS) return 'invalid_shape';
      area+=(a.lng-path[0].lng)*(b.lat-path[0].lat)-(b.lng-path[0].lng)*(a.lat-path[0].lat);
      for(let j=i+1;j<path.length;j++) {
        if(j===i+1 || i===0 && j===path.length-1) continue;
        if(intersects(a,b,path[j],path[(j+1)%path.length])) return 'invalid_shape';
      }
    }
    if(Math.abs(area)<EPS) return 'invalid_shape';
  }
  return null;
}

/**
 * Area of union(lawns) minus union(exclusions), restricted to the lawns.
 * A local cylindrical equal-area projection keeps measurements in square metres.
 * Split at every vertex and edge crossing. Between consecutive cuts all boundary
 * lines keep their order, so union/intersection lengths are linear and their
 * midpoint integral gives the strip area without raster sampling or double counting.
 */
export function calculateLawnAreas(shapes) {
  const radius=6378137,rad=Math.PI/180,origin=shapes[0]?.path[0];
  if(!origin)return {gross:0,excluded:0,net:0};
  const sinOrigin=Math.sin(origin.lat*rad);
  const polygons=shapes.map(shape=>({kind:shape.kind,path:shape.path.map(p=>({
    x:radius*((p.lng-origin.lng+540)%360-180)*rad,
    y:radius*(Math.sin(p.lat*rad)-sinOrigin)
  }))}));
  const cuts=[],edges=[];
  for(const polygon of polygons) {
    polygon.edges=[];
    polygon.path.forEach((a,i)=>{
      const b=polygon.path[(i+1)%polygon.path.length];cuts.push(a.x);
      const edge={a,b,dx:b.x-a.x,dy:b.y-a.y};
      edges.push(edge);polygon.edges.push(edge);
    });
  }
  // Collinear edges change order only at existing endpoints, already included above.
  for(let i=0;i<edges.length;i++)for(let j=i+1;j<edges.length;j++) {
    const a=edges[i],b=edges[j],det=a.dx*b.dy-a.dy*b.dx;
    if(Math.abs(det)<=Number.EPSILON*32*(Math.abs(a.dx*b.dy)+Math.abs(a.dy*b.dx)))continue;
    const x=b.a.x-a.a.x,y=b.a.y-a.a.y;
    const t=(x*b.dy-y*b.dx)/det,u=(x*a.dy-y*a.dx)/det;
    if(t>0&&t<1&&u>0&&u<1)cuts.push(a.a.x+t*a.dx);
  }
  cuts.sort((a,b)=>a-b);
  const unionAt=(kind,x)=>{
    const intervals=[];
    for(const polygon of polygons) {
      if(polygon.kind!==kind)continue;
      const ys=[];
      for(const {a,b,dx,dy} of polygon.edges) {
        if(x>Math.min(a.x,b.x)&&x<Math.max(a.x,b.x))ys.push(a.y+(x-a.x)*dy/dx);
      }
      ys.sort((a,b)=>a-b);
      for(let i=0;i+1<ys.length;i+=2)intervals.push([ys[i],ys[i+1]]);
    }
    intervals.sort((a,b)=>a[0]-b[0]);
    const merged=[];
    for(const interval of intervals) {
      const previous=merged[merged.length-1];
      if(previous&&interval[0]<=previous[1])previous[1]=Math.max(previous[1],interval[1]);
      else merged.push(interval);
    }
    return merged;
  };
  let gross=0,excluded=0;
  for(let i=1;i<cuts.length;i++) {
    const width=cuts[i]-cuts[i-1];if(width<=1e-8)continue;
    const x=(cuts[i]+cuts[i-1])/2,lawns=unionAt('lawn',x),holes=unionAt('exclude',x);
    gross+=width*lawns.reduce((sum,[a,b])=>sum+b-a,0);
    let l=0,h=0;
    while(l<lawns.length&&h<holes.length) {
      const a=lawns[l],b=holes[h];
      excluded+=width*Math.max(0,Math.min(a[1],b[1])-Math.max(a[0],b[0]));
      if(a[1]<b[1])l++;else h++;
    }
  }
  excluded=Math.min(gross,Math.max(0,excluded));
  return {gross,excluded,net:Math.max(0,gross-excluded)};
}

let sdkPromise;
let authHookInstalled=false;
let authenticationFailed=false;
async function loadMaps(key) {
  if(!authHookInstalled) {
    authHookInstalled=true;
    const previous=window.gm_authFailure;
    window.gm_authFailure=function(...args) {
      authenticationFailed=true;window.dispatchEvent(new Event('kr:lawn-map-auth-failure'));
      if(typeof previous==='function')previous.apply(this,args);
    };
  }
  if(!sdkPromise) {
    sdkPromise=(async()=>{
      if(!window.google?.maps?.importLibrary) {
        await new Promise((resolve,reject)=>{
          let script=document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
          let timer,poll;
          const clear=()=>{clearTimeout(timer);clearInterval(poll);};
          timer=setTimeout(()=>{clear();reject(new Error('maps_timeout'));},20000);
          if(script) {
            poll=setInterval(()=>{if(window.google?.maps?.importLibrary){clear();resolve();}},100);
            return;
          }
          script=document.createElement('script');script.async=true;
          const url=new URL('https://maps.googleapis.com/maps/api/js');
          for(const [name,value] of Object.entries({key,v:'weekly',loading:'async',callback:'__krLawnMapsReady',language:'lt',region:'LT'})) url.searchParams.set(name,value);
          window.__krLawnMapsReady=()=>{clear();delete window.__krLawnMapsReady;resolve();};
          script.onerror=()=>{clear();script.remove();delete window.__krLawnMapsReady;reject(new Error('maps_load'));};
          script.src=url.toString();document.head.append(script);
        });
      }
      const [maps,geocoding]=await Promise.all(['maps','geocoding'].map(name=>window.google.maps.importLibrary(name)));
      return {...maps,...geocoding};
    })().catch(error=>{sdkPromise=null;throw error;});
  }
  return sdkPromise;
}

class LawnMap extends Component {
  shapes=[];
  listeners=[];
  linked=false;
  generation=0;
  connectedCallback() {
    super.connectedCallback();
    this.abort?.abort();this.abort=new AbortController();
    this.copy=JSON.parse(this.querySelector('[data-map-copy]').textContent);
    this.finder=this.closest('kr-mower-finder');this.form=this.finder.querySelector('form');
    const options={signal:this.abort.signal};
    this.querySelector('details').addEventListener('toggle',()=>{if(this.querySelector('details').open)this.initialize();},options);
    this.addEventListener('click',event=>{
      const action=event.target.closest('[data-map-action]')?.dataset.mapAction;
      if(!action)return;
      if(action==='search')this.search();
      if(action==='lawn'||action==='exclude')this.start(action);
      if(action==='finish')this.finish();
      if(action==='undo')this.undo();
      if(action==='cancel')this.cancel();
      if(action==='center' && this.draft)this.addPoint(this.map.getCenter());
      if(action==='clear')this.reset();
      if(action==='delete')this.remove(Number(event.target.closest('[data-shape]').dataset.shape));
    },options);
    this.querySelector('[data-map-address]').addEventListener('keydown',event=>{
      if(event.key==='Enter'){event.preventDefault();event.stopPropagation();this.search();}
    },options);
    this.finder.addEventListener('kr:area-manual',()=>{this.linked=false;this.status('manual');},options);
    window.addEventListener('kr:lawn-map-auth-failure',()=>{
      this.ready=false;this.linked=false;this.cancel();
      this.querySelector('[data-map-tools]').hidden=true;this.querySelector('[data-map-search]').hidden=true;
      this.querySelector('[data-map-canvas]').hidden=true;this.status('load_error');
    },options);
    if(this.querySelector('details').open)this.initialize();
  }
  disconnectedCallback() {
    this.generation++;this.abort?.abort();this.ready=false;this.linked=false;
    this.cancel();this.shapes.forEach(s=>this.dispose(s));this.shapes=[];
    this.listeners.forEach(l=>l.remove());this.listeners=[];
    this.map=null;this.ready=false;this.loading=false;
    super.disconnectedCallback();
  }
  status(key,values={}) {
    this.updateTools();
    let text=this.copy[key]||key;
    for(const [name,value] of Object.entries(values))text=text.replaceAll(`{${name}}`,value);
    this.querySelector('[data-map-status]').textContent=text;
  }
  updateTools() {
    const drawing=Boolean(this.draft),hasLawn=this.shapes.some(s=>s.kind==='lawn');
    this.querySelector('[data-map-action="lawn"]').disabled=drawing;
    this.querySelector('[data-map-action="exclude"]').disabled=drawing||!hasLawn;
    this.querySelector('[data-map-action="clear"]').hidden=!this.shapes.length||drawing;
  }
  async initialize() {
    if(this.ready||this.loading)return;
    this.loading=true;const generation=this.generation;this.status('loading');
    try {
      this.api=await loadMaps(this.dataset.apiKey);
      if(authenticationFailed)throw new Error('maps_auth');
      if(!this.isConnected||this.generation!==generation)return;
      const canvas=this.querySelector('[data-map-canvas]');canvas.hidden=false;
      this.map=new this.api.Map(canvas,{center:{lat:54.8985,lng:23.9036},zoom:15,mapTypeId:'hybrid',tilt:0,streetViewControl:false,fullscreenControl:true,gestureHandling:'cooperative',keyboardShortcuts:true});
      this.geocoder=new this.api.Geocoder();
      this.listeners.push(this.map.addListener('click',event=>{if(this.draft&&event.latLng)this.addPoint(event.latLng);}));
      this.ready=true;this.querySelector('[data-map-tools]').hidden=false;
      this.querySelector('[data-map-search]').hidden=false;this.status('ready');
    } catch(error) {
      if(this.isConnected&&this.generation===generation)this.status('load_error');
    } finally {if(this.generation===generation)this.loading=false;}
  }
  async search() {
    if(!this.ready||this.searching)return;
    const input=this.querySelector('[data-map-address]'),address=input.value.trim();
    if(address.length<3){this.status('address_required');input.focus();return;}
    const generation=this.generation;this.searching=true;
    const button=this.querySelector('[data-map-action="search"]');button.disabled=true;this.status('searching');
    try {
      const {results}=await this.geocoder.geocode({address,region:'LT'});
      if(!this.isConnected||this.generation!==generation||input.value.trim()!==address)return;
      const result=results?.[0];if(!result){this.status('not_found');return;}
      this.map.setCenter(result.geometry.location);this.map.setZoom(20);
      this.status('found',{address:result.formatted_address});
    } catch(error) {if(this.isConnected&&this.generation===generation)this.status(error.code==='ZERO_RESULTS'?'not_found':'search_error');}
    finally {this.searching=false;if(this.isConnected)button.disabled=false;}
  }
  polygon(kind,path=[]) {
    // Keep a first ring even when empty; paths:[] represents zero rings in Maps.
    return new this.api.Polygon({map:this.map,paths:[path],strokeColor:kind==='lawn'?'#31743c':'#b33232',fillColor:kind==='lawn'?'#58a65c':'#db6969',strokeWeight:3,fillOpacity:.24,clickable:false});
  }
  start(kind) {
    if(!this.ready)return;
    if(this.draft){this.status('finish_first');return;}
    if(kind==='exclude'&&!this.shapes.some(s=>s.kind==='lawn')){this.status('need_lawn');return;}
    this.linked=true;this.draft={kind,polygon:this.polygon(kind)};
    this.querySelector('[data-map-drawing]').hidden=false;this.dataset.drawing='true';
    this.querySelector('[data-map-action="finish"]').disabled=true;
    this.status('drawing',{count:0});
  }
  addPoint(point) {
    this.draft.polygon.getPath().push(point);
    const count=this.draft.polygon.getPath().getLength();
    this.querySelector('[data-map-action="finish"]').disabled=count<3;
    this.status('drawing',{count});
  }
  finish() {
    if(!this.draft||this.draft.polygon.getPath().getLength()<3)return;
    const shape=this.draft;this.draft=null;shape.polygon.setEditable(true);shape.listeners=[];
    for(const event of ['insert_at','set_at','remove_at'])shape.listeners.push(shape.polygon.getPath().addListener(event,()=>{this.linked=true;this.sync();}));
    this.shapes.push(shape);this.querySelector('[data-map-drawing]').hidden=true;delete this.dataset.drawing;
    this.sync();
  }
  cancel() {
    if(this.draft){this.draft.polygon.setMap(null);this.draft=null;}
    const controls=this.querySelector('[data-map-drawing]');if(controls)controls.hidden=true;
    delete this.dataset.drawing;
    if(!this.shapes.length)this.linked=false;
    if(this.ready)this.sync();
  }
  undo() {if(this.draft){const path=this.draft.polygon.getPath();if(path.getLength())path.pop();this.querySelector('[data-map-action="finish"]').disabled=path.getLength()<3;this.status('drawing',{count:path.getLength()});}}
  dispose(shape) {shape.listeners?.forEach(l=>l.remove());shape.polygon.setMap(null);}
  remove(index) {const [shape]=this.shapes.splice(index,1);if(shape)this.dispose(shape);if(!this.shapes.length)this.reset();else this.sync();}
  reset() {
    const wasLinked=this.linked;this.linked=false;this.cancel();this.shapes.forEach(shape=>this.dispose(shape));this.shapes=[];
    if(wasLinked){this.form.elements.area.value='';this.dispatchEvent(new CustomEvent('kr:map-area',{bubbles:true,detail:{area:null}}));}
    this.querySelector('[data-map-shapes]').replaceChildren();this.querySelector('[data-map-area]').textContent='';
    this.error=null;if(this.ready)this.status('ready');
  }
  values() {return this.shapes.map(shape=>({kind:shape.kind,path:shape.polygon.getPath().getArray().map(p=>({lat:p.lat(),lng:p.lng()}))}));}
  get validationMessage() {return this.querySelector('[data-map-status]').textContent;}
  clearInvalidArea() {
    if(!this.linked)return;
    this.form.elements.area.value='';
    this.dispatchEvent(new CustomEvent('kr:map-area',{bubbles:true,detail:{area:null}}));
  }
  sync() {
    const values=this.values();
    this.error=validateLawnShapes(values);
    const list=this.querySelector('[data-map-shapes]');list.replaceChildren();
    this.shapes.forEach((shape,index)=>{
      const item=document.createElement('li');item.dataset.shape=index;
      const label=document.createElement('span');label.textContent=`${this.copy[shape.kind]} ${index+1}`;
      const button=document.createElement('button');button.type='button';button.dataset.mapAction='delete';button.textContent=this.copy.remove;button.setAttribute('aria-label',`${this.copy.remove}: ${label.textContent}`);
      item.prepend(label);item.append(button);list.append(item);
    });
    if(this.error){
      this.clearInvalidArea();this.querySelector('[data-map-area]').textContent='';
      this.status(this.error);
      return;
    }
    const {gross,excluded,net}=calculateLawnAreas(values);
    // Suppress sub-micrometre numerical noise at exact whole-square-metre boundaries.
    const total=Math.ceil(net-1e-6);
    const field=this.form.elements.area;
    if(total<Number(field.dataset.min)||total>Number(field.dataset.max)){this.error='area_range';this.clearInvalidArea();this.status('area_range');this.querySelector('[data-map-area]').textContent='';return;}
    const number=n=>n.toLocaleString('lt-LT',{maximumFractionDigits:2});
    this.querySelector('[data-map-area]').textContent=this.copy.total.replace('{sqm}',number(total)).replace('{ares}',number(total/100)).replace('{gross}',number(gross)).replace('{excluded}',number(excluded));
    if(this.linked){field.value=String(total);field.setCustomValidity('');this.form.elements.area_unit.value='m2';this.dispatchEvent(new CustomEvent('kr:map-area',{bubbles:true,detail:{area:total}}));}
    this.status(this.linked?'applied':'manual');
  }
  validate() {
    if(!this.linked)return true;
    if(this.draft){this.status('finish_first');return false;}
    if(this.error){this.sync();return false;}
    return true;
  }
}
if(!customElements.get('kr-lawn-map'))customElements.define('kr-lawn-map',LawnMap);
